import React, { forwardRef, useImperativeHandle, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import {
  Grid,
  Box,
  TextField,
  MenuItem,
  Typography,
  Chip,
} from "@mui/material";
import { Description as DescriptionIcon, Download as DownloadIcon, Close as CloseIcon } from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import i18next from "i18next";
import "dayjs/locale/tr";
import { useVerifyTcNoMutation } from '../../../../store/api/authSlice';
import { useCheckEligibilityMutation } from '../../../../store/api/doctorApiSlice';
import { useLazyGetPrivateFileQuery } from '../../../../store/api/fileApiSlice';
import { cityOptions, CityType } from '../../../../constants/cityTypes';
import { toast } from 'react-toastify';
import dayjs from "dayjs";
import { validateTCKN } from '../../../../utils/tcknUtils';
import { formatBirthDateOnly, parseBirthDateForPicker } from '../../../../utils/dateUtils';
import { appColors } from "../../../../constants/theme";
import { resolveStoredFileId } from "../../../../utils/storedFile";
import { stepTitleSx } from "../style";


const RegisterStepOne = forwardRef(({ onSuccess, onLoadingChange, onEligibilityResult, isDoctorCategory = true, isEnabiz = true }, ref) => {
  const profile = useSelector((state) => state.doctor.profile);
  const [verifyTcNo] = useVerifyTcNoMutation();
  const [checkEligibility] = useCheckEligibilityMutation();
  const [getPrivateFile] = useLazyGetPrivateFileQuery();
  const [documentLoadingFileName, setDocumentLoadingFileName] = useState(null);
  const [removedGraduationPdf, setRemovedGraduationPdf] = useState(false);
  const [removedCertificateFileNames, setRemovedCertificateFileNames] = useState([]);
  const { t } = useTranslation();

  const existingGraduationPdfFileName = profile?.graduationPdfFileName ?? profile?.GraduationPdfFileName;
  const existingCertificateFileNames = profile?.certificateFileNames ?? profile?.CertificateFileNames ?? [];
  const existingCertificateList = (Array.isArray(existingCertificateFileNames) ? existingCertificateFileNames : [])
    .filter((fn) => !removedCertificateFileNames.includes(fn));

  const handleOpenDocument = async (fileName) => {
    if (!fileName) return;
    setDocumentLoadingFileName(fileName);
    try {
      const fileId = resolveStoredFileId(fileName);
      if (!fileId) throw new Error("invalid_file_id");
      const url = await getPrivateFile(fileId).unwrap();
      if (url && typeof url === "string") window.open(url, "_blank");
    } catch (e) {
      toast.error(t("file_fetch_error") || "Dosya açılamadı.");
    } finally {
      setDocumentLoadingFileName(null);
    }
  };

  const inputFieldStyle = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
    },
  };


  const parseCity = (val) => {
    if (val === undefined || val === null || val === '') return '';
    if (typeof val === 'number') return val;
    if (typeof val === 'string' && /^\d+$/.test(val)) return Number(val);
    if (typeof val === 'string' && CityType[val] !== undefined) return CityType[val];
    const byLabel = cityOptions.find((o) => o.label === val);
    return byLabel ? byLabel.value : '';
  };

  const handleCheckDoctorEligibility = async (values) => {
    try {
      // Form'dan gelen gerçek verileri kullan
      const eligibilityData = {
        seriNo: values.identitySerialNo,
        tckn: values.identityNumber,
        tescilNo: values.diplomaNo
      };
      console.log("eligibilityData********************");
      console.log(eligibilityData);
      const res = await checkEligibility(eligibilityData).unwrap();
      if (res.isSuccess) {
        return res.data === true;
      } else {
        return false;
      }
    } catch (error) {
      console.error("Uygunluk kontrolü hatası:", error);
      return false;
    }
  };

  const defaultBirthDate = dayjs().subtract(21, "year").startOf("year");

  const resolveBirthDate = (value) => {
    if (!value) return defaultBirthDate;
    const parsed = parseBirthDateForPicker(value);
    if (!parsed || !parsed.isValid()) return defaultBirthDate;
    if (parsed.year() <= 1900) return defaultBirthDate;
    return parsed;
  };

  const formik = useFormik({
    initialValues: {
      firstName: profile?.firstName || "",
      lastName: profile?.lastName || "",
      birthDate: resolveBirthDate(profile?.birthDate),
      city: parseCity(profile?.city),
      gender: profile?.gender && ["Male", "Female"].includes(profile.gender) ? profile.gender : "",
      diplomaNo: profile?.diplomaNo || "",
      identitySerialNo: profile?.identitySerialNo || "",
      identityNumber: profile?.identityNumber || "",
      graduationPdfFile: null,
      certificateFiles: [],
      existingGraduationPdfFileName: existingGraduationPdfFileName || null,
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      firstName: Yup.string().required(t("required_field")),
      lastName: Yup.string().required(t("required_field")),
      birthDate: Yup.date()
        .required(t("required_field"))
        .nullable()
        .max(dayjs().subtract(21, "year").endOf("day").toDate(), "En az 21 yaşında olmalısınız"),
      identityNumber: Yup.string()
        .required(t("required_field"))
        .test('tckn-full-validation', t("validIdentity"), function (value) {
          if (!value) return false;
          const result = validateTCKN(value);
          return result.isValid;
        }),
      city: Yup.number().typeError(t("required_field")).required(t("required_field")),
      gender: Yup.string()
        .oneOf(["Male", "Female"], t("required_field"))
        .required(t("required_field")),
      ...(isDoctorCategory && isEnabiz
        ? {
            diplomaNo: Yup.string()
              .required(t("required_field"))
              .matches(/^\d{6}$/, t("length_number_warning", { item: 6 })),
            identitySerialNo: Yup.string()
              .required(t("required_field"))
              .matches(/^\d{4}$/, t("length_number_warning", { item: 4 })),
          }
        : isDoctorCategory
        ? {
            identitySerialNo: Yup.string().optional(),
          }
        : {
            identitySerialNo: Yup.string()
              .required(t("required_field"))
              .matches(/^\d{4}$/, t("length_number_warning", { item: 4 })),
            graduationPdfFile: Yup.mixed()
              .nullable()
              .test("required-pdf", t("required_field"), function (value) {
                if (isDoctorCategory) return true;
                if (value instanceof File) return true;
                const existing = this.parent?.existingGraduationPdfFileName;
                if (existing) return true;
                return false;
              }),
          }),
    }),
    onSubmit: async (values) => {
      try {
        if (onLoadingChange) onLoadingChange(true);

        const birthOnly = formatBirthDateOnly(values.birthDate);
        const dogumYili = birthOnly
          ? parseInt(birthOnly.slice(0, 4), 10)
          : (values.birthDate && typeof values.birthDate?.year === 'function'
              ? values.birthDate.year()
              : new Date(values.birthDate).getFullYear());
        const tcPayload = {
          TCKimlikNo: Number(values.identityNumber),
          Ad: values.firstName.trim(),
          Soyad: values.lastName.trim(),
          DogumYili: dogumYili,
        };

        const tcResponse = await verifyTcNo(tcPayload).unwrap();
        if (!tcResponse.isSuccess) {
          toast.error(t("id_verification_failed"));
          return;
        }

        if (isDoctorCategory && isEnabiz) {
          const eligibilityResult = await handleCheckDoctorEligibility(values);
          if (eligibilityResult === false) return;
        }

        if (onSuccess) {
          onSuccess({
            ...values,
            removedCertificateFileNames: removedCertificateFileNames.length > 0 ? removedCertificateFileNames : undefined,
            removedGraduationPdf: removedGraduationPdf || undefined,
          });
        }
      } catch (error) {
        console.error("Form submission error:", error);
        toast.error(t("id_verification_failed"));
      } finally {
        if (onLoadingChange) onLoadingChange(false);
      }
    },
  });

  // Profile değişikliklerini izle ve form values'ı güncelle
  useEffect(() => {
    if (profile) {
      formik.setFieldValue('firstName', profile.firstName || '');
      formik.setFieldValue('lastName', profile.lastName || '');
      formik.setFieldValue('birthDate', resolveBirthDate(profile.birthDate));
      formik.setFieldValue('city', parseCity(profile.city));
      formik.setFieldValue('gender', profile.gender && ["Male", "Female"].includes(profile.gender) ? profile.gender : "");
      // formik.setFieldValue('diplomaNo', profile.diplomaNo || "");
      // formik.setFieldValue('identitySerialNo', profile.identitySerialNo || "");
      // formik.setFieldValue('identityNumber', profile.identityNumber || '');
      formik.setFieldValue('diplomaNo', "192837");
      formik.setFieldValue('identitySerialNo', "9746");
      formik.setFieldValue('identityNumber', "56344868790");
      const existingPdf = profile.graduationPdfFileName ?? profile.GraduationPdfFileName;
      formik.setFieldValue('existingGraduationPdfFileName', existingPdf || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const runEligibilityCheck = async () => {
    if (!isDoctorCategory || !isEnabiz) return true;
    const errors = await formik.validateForm();
    const requiredFields = ["identityNumber", "identitySerialNo", "diplomaNo"];
    const hasRequiredErrors = requiredFields.some((field) => Boolean(errors[field]));
    if (hasRequiredErrors) {
      formik.setTouched({
        identityNumber: true,
        identitySerialNo: true,
        diplomaNo: true,
      });
      const firstErrorKey = requiredFields.find((field) => errors[field]);
      const el = firstErrorKey ? document.querySelector(`[name="${firstErrorKey}"]`) : null;
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (typeof el.focus === 'function') el.focus();
      }
      if (onEligibilityResult) onEligibilityResult(null);
      return false;
    }
    const result = await handleCheckDoctorEligibility(formik.values);
    if (onEligibilityResult) onEligibilityResult(result === true);
    return result === true;
  };

  useEffect(() => {
    if (onEligibilityResult) {
      onEligibilityResult(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.identityNumber, formik.values.identitySerialNo, formik.values.diplomaNo]);

  useImperativeHandle(ref, () => ({
    handleSubmit: async () => {
      const errors = await formik.validateForm();
      if (Object.keys(errors).length > 0) {
        const touched = {
          firstName: true,
          lastName: true,
          birthDate: true,
          identityNumber: true,
          city: true,
          gender: true,
          identitySerialNo: true,
        };
        if (isDoctorCategory) touched.diplomaNo = true;
        else touched.graduationPdfFile = true;
        formik.setTouched(touched);
        const firstErrorKey = Object.keys(errors)[0];
        const el = document.querySelector(`[name="${firstErrorKey}"]`);
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (typeof el.focus === 'function') el.focus();
        }
        return;
      }
      formik.handleSubmit();
    },
    checkEligibility: runEligibilityCheck,
  }));

  const {
    values,
    errors,
    touched,
    handleChange,
    setFieldValue,
  } = formik;

  return (
    <Box sx={{ width: '100%', boxSizing: 'border-box' }}>
      <form onSubmit={formik.handleSubmit} style={{ width: '100%' }}>
        <Box sx={{ width: '100%', boxSizing: 'border-box' }}>
          <Typography variant="h6" sx={stepTitleSx}>
            Temel Bilgiler
          </Typography>

          <Grid
            container
            spacing={3}
            sx={{
              width: '100%'
            }}
          >
            {/* Ad */}
            <Grid size={{ xs: 12, sm: 6, md: 5.8 }}>
              <TextField
                fullWidth
                variant="outlined"
                id="firstName"
                name="firstName"
                label={t("first_name")}
                placeholder={t("first_name_placeholder")}
                value={values.firstName}
                onChange={handleChange}
                error={touched.firstName && Boolean(errors.firstName)}
                helperText={touched.firstName && errors.firstName}
                sx={inputFieldStyle}
              />
            </Grid>

            {/* Soyad */}
            <Grid size={{ xs: 12, sm: 6, md: 5.8 }}>
              <TextField
                fullWidth
                variant="outlined"
                id="lastName"
                name="lastName"
                label={t("last_name")}
                placeholder={t("last_name_placeholder")}
                value={values.lastName}
                onChange={handleChange}
                error={touched.lastName && Boolean(errors.lastName)}
                helperText={touched.lastName && errors.lastName}
                sx={inputFieldStyle}
              />
            </Grid>

            {/* Doğum Tarihi */}
            <Grid size={{ xs: 12, sm: 6, md: 5.8 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18next.language}>
                <DatePicker
                  label={t("birth_date")}
                  value={formik.values.birthDate}
                  onChange={(val) => formik.setFieldValue("birthDate", val)}
                  format="DD/MM/YYYY"
                  maxDate={dayjs()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: "outlined",
                      placeholder: t("date_format_placeholder"),
                      error: touched.birthDate && Boolean(errors.birthDate),
                      helperText: touched.birthDate && errors.birthDate,
                      sx: inputFieldStyle
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            {/* TC Kimlik No */}
            <Grid size={{ xs: 12, sm: 6, md: 5.8 }}>
              <TextField
                fullWidth
                variant="outlined"
                id="identityNumber"
                name="identityNumber"
                label={t("identity_number")}
                placeholder={t("tc_identity_number_placeholder")}
                value={values.identityNumber}
                onChange={handleChange}
                error={touched.identityNumber && Boolean(errors.identityNumber)}
                helperText={touched.identityNumber && errors.identityNumber}
                inputProps={{ maxLength: 11, inputMode: "numeric" }}
                sx={inputFieldStyle}
              />
            </Grid>


            {isDoctorCategory && isEnabiz && (
              <Grid size={{ xs: 12, sm: 6, md: 5.8 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  id="diplomaNo"
                  name="diplomaNo"
                  label={t("tescilNo")}
                  placeholder={t("diploma_number_placeholder")}
                  value={values.diplomaNo}
                  onChange={handleChange}
                  error={touched.diplomaNo && Boolean(errors.diplomaNo)}
                  helperText={touched.diplomaNo && errors.diplomaNo}
                  inputProps={{ maxLength: 6, inputMode: "numeric" }}
                  sx={inputFieldStyle}
                />
              </Grid>
            )}

            {/* Seri No (Nüfus Seri No) - Enabiz uygunluk için; isEnabiz değilse gizlenir */}
            {(!isDoctorCategory || isEnabiz) && (
              <Grid size={{ xs: 12, sm: 6, md: 5.8 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  id="identitySerialNo"
                  name="identitySerialNo"
                  label={t("identity_serial_number")}
                  placeholder={t("population_serial_number_placeholder")}
                  value={values.identitySerialNo}
                  onChange={handleChange}
                  error={touched.identitySerialNo && Boolean(errors.identitySerialNo)}
                  helperText={touched.identitySerialNo && errors.identitySerialNo}
                  inputProps={{ maxLength: 4, inputMode: "numeric" }}
                  sx={inputFieldStyle}
                />
              </Grid>
            )}

            {!isDoctorCategory && (
              <>
                <Grid size={{ xs: 12, sm: 6, md: 5.8 }}>
                  <TextField
                    fullWidth
                    type="file"
                    name="graduationPdfFile"
                    label={t("graduation_pdf_required")}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ accept: ".pdf" }}
                    InputProps={{ sx: { borderRadius: '12px' } }}
                    error={touched.graduationPdfFile && Boolean(errors.graduationPdfFile)}
                    helperText={touched.graduationPdfFile && errors.graduationPdfFile}
                    sx={inputFieldStyle}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      setFieldValue("graduationPdfFile", file || null);
                    }}
                  />
                  {values.graduationPdfFile instanceof File ? (
                    <Chip
                      icon={<DescriptionIcon sx={{ fontSize: 16 }} />}
                      label={values.graduationPdfFile.name}
                      variant="outlined"
                      size="small"
                      onDelete={() => setFieldValue("graduationPdfFile", null)}
                      deleteIcon={<CloseIcon sx={{ fontSize: 16 }} />}
                      sx={{ mt: 1, borderColor: appColors.primary, color: appColors.primary, '& .MuiChip-deleteIcon': { color: appColors.gray500, '&:hover': { color: appColors.errorDark } } }}
                    />
                  ) : (existingGraduationPdfFileName && !removedGraduationPdf) ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                      <Chip
                        icon={<DescriptionIcon sx={{ fontSize: 16 }} />}
                        label={t("graduation_pdf_document") || "Mezuniyet Belgesi"}
                        variant="outlined"
                        size="small"
                        deleteIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                        onDelete={() => handleOpenDocument(existingGraduationPdfFileName)}
                        disabled={documentLoadingFileName === existingGraduationPdfFileName}
                        sx={{ borderColor: appColors.primary, color: appColors.primary, '& .MuiChip-deleteIcon': { color: appColors.primary } }}
                      />
                      <CloseIcon
                        sx={{ fontSize: 18, color: appColors.gray500, cursor: 'pointer', '&:hover': { color: appColors.errorDark } }}
                        onClick={() => {
                          setRemovedGraduationPdf(true);
                          setFieldValue("existingGraduationPdfFileName", null);
                        }}
                      />
                    </Box>
                  ) : null}
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 5.8 }}>
                  <TextField
                    fullWidth
                    type="file"
                    name="certificateFiles"
                    label={t("certificates_optional")}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ accept: ".pdf", multiple: true }}
                    InputProps={{ sx: { borderRadius: '12px' } }}
                    sx={inputFieldStyle}
                    onChange={(e) => {
                      const files = e.target.files ? Array.from(e.target.files) : [];
                      setFieldValue("certificateFiles", [...(values.certificateFiles || []), ...files]);
                    }}
                  />
                  {(existingCertificateList.length > 0 || (Array.isArray(values.certificateFiles) && values.certificateFiles.length > 0)) && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                      {existingCertificateList.map((fileName, idx) => (
                        <Box key={`existing-${idx}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                          <Chip
                            icon={<DescriptionIcon sx={{ fontSize: 14 }} />}
                            label={`${t("certificate") || "Sertifika"} ${idx + 1}`}
                            variant="outlined"
                            size="small"
                            deleteIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                            onDelete={() => handleOpenDocument(fileName)}
                            disabled={documentLoadingFileName === fileName}
                            sx={{ borderColor: appColors.primary, color: appColors.primary, '& .MuiChip-deleteIcon': { color: appColors.primary } }}
                          />
                          <CloseIcon
                            sx={{ fontSize: 16, color: appColors.gray500, cursor: 'pointer', '&:hover': { color: appColors.errorDark } }}
                            onClick={() => setRemovedCertificateFileNames((prev) => [...prev, fileName])}
                          />
                        </Box>
                      ))}
                      {Array.isArray(values.certificateFiles) && values.certificateFiles.map((file, idx) => (
                        <Chip
                          key={`new-${idx}`}
                          icon={<DescriptionIcon sx={{ fontSize: 14 }} />}
                          label={file.name}
                          variant="outlined"
                          size="small"
                          onDelete={() => {
                            const updated = values.certificateFiles.filter((_, i) => i !== idx);
                            setFieldValue("certificateFiles", updated);
                          }}
                          deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                          sx={{ borderColor: appColors.primary, color: appColors.primary, '& .MuiChip-deleteIcon': { color: appColors.gray500, '&:hover': { color: appColors.errorDark } } }}
                        />
                      ))}
                    </Box>
                  )}
                </Grid>
              </>
            )}

            {/* Şehir */}
            <Grid size={{ xs: 12, sm: 6, md: 5.8 }}>
              <TextField
                fullWidth
                variant="outlined"
                select
                id="city"
                name="city"
                placeholder={t("city_select_placeholder")}
                value={values.city || ""}
                onChange={(e) => {
                  const numeric = e.target.value === '' ? '' : Number(e.target.value);
                  setFieldValue('city', numeric);
                }}
                error={touched.city && Boolean(errors.city)}
                helperText={touched.city && errors.city}
                sx={inputFieldStyle}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected) => {
                    if (!selected) return t("city_select_placeholder");
                    const cityOption = cityOptions.find((c) => c.value === selected);
                    return cityOption ? cityOption.label : selected;
                  },
                }}
              >
                <MenuItem value="" disabled sx={{ fontWeight: "normal" }}>
                  {t("city_select_placeholder")}
                </MenuItem>
                {cityOptions.map((c) => (
                  <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Cinsiyet */}
            <Grid size={{ xs: 12, sm: 6, md: 5.8 }}>
              <TextField
                fullWidth
                variant="outlined"
                select
                id="gender"
                name="gender"
                placeholder={t("gender_select_placeholder")}
                value={["Male", "Female"].includes(values.gender) ? values.gender : ""}
                onChange={(e) => {
                  setFieldValue('gender', e.target.value);
                }}
                error={touched.gender && Boolean(errors.gender)}
                helperText={touched.gender && errors.gender}
                sx={inputFieldStyle}
                SelectProps={{
                  displayEmpty: true,
                }}
              >
                <MenuItem value="" disabled sx={{ fontWeight: "normal" }}>
                  {t("gender_select_placeholder")}
                </MenuItem>
                <MenuItem value="Male">{t("male")}</MenuItem>
                <MenuItem value="Female">{t("female")}</MenuItem>
              </TextField>
            </Grid>


          </Grid>
        </Box>
      </form>
    </Box>
  );
});

export default RegisterStepOne;